import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";
import {
  fetchPastPopulationChanges,
  isIncreasePopulation,
  PopulationPoint,
} from "src/site/estat";
import { ChartSpec, debounce, isSp, renderChart } from "src/site/recharts";

// Site::PrefectureArea::PopulationChange::Component の Stimulus コントローラ。
// landIeul: components/domainParts/PopulationChange/index.tsx。
// 過去分は e-Stat、将来予測は GetPopulationCountByPrefecture (GraphQL) から取得して連結する。

const QUERY = `query GetPopulationCountByPrefecture {
  populationCountByPrefectures {
    prefecture { id name }
    year
    population
  }
}`;

interface PopulationCountRow {
  prefecture: { id: string; name: string };
  year: number;
  population: number;
}

class SitePrefectureAreaPopulationChangeController extends Controller<HTMLElement> {
  static targets = ["graph", "descriptionIncrease", "descriptionDecrease"];
  static values = { areaCode: String, areaId: String, graphql: String };

  declare readonly graphTarget: HTMLElement;
  declare readonly descriptionIncreaseTarget: HTMLElement;
  declare readonly descriptionDecreaseTarget: HTMLElement;
  declare readonly areaCodeValue: string;
  declare readonly areaIdValue: string;
  declare readonly graphqlValue: string;

  private graphData: PopulationPoint[] = [];
  private onResize?: () => void;

  connect(): void {
    this.onResize = debounce(() => this.draw());
    window.addEventListener("resize", this.onResize);
    void this.load();
  }

  disconnect(): void {
    if (this.onResize) window.removeEventListener("resize", this.onResize);
  }

  private async load(): Promise<void> {
    const [past, future] = await Promise.all([
      fetchPastPopulationChanges(this.areaCodeValue),
      this.loadFuture(),
    ]);
    if (!past) return;
    const populationData = past.concat(future);
    const increase = isIncreasePopulation(populationData);
    this.descriptionIncreaseTarget.hidden = !increase;
    this.descriptionDecreaseTarget.hidden = increase;
    this.graphData = populationData;
    this.draw();
  }

  private async loadFuture(): Promise<PopulationPoint[]> {
    try {
      const res = await fetch(this.graphqlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: QUERY }),
      });
      const json = await res.json();
      const rows: PopulationCountRow[] =
        json?.data?.populationCountByPrefectures || [];
      return rows
        .filter((row) => row.prefecture.id === this.areaIdValue)
        .map((row) => ({ population: row.population, year: row.year }));
    } catch {
      return [];
    }
  }

  private draw(): void {
    if (this.graphData.length === 0) return;
    renderChart(this.graphTarget, this.spec());
  }

  private spec(): ChartSpec {
    const sp = isSp();
    return {
      kind: "line",
      data: this.graphData as unknown as ChartSpec["data"],
      xKey: "year",
      xPadding: { left: 20, right: 20 },
      xTickFontSize: "10px",
      yTickFontSize: "10px",
      yUnit: "千人",
      yLocaleString: true,
      curve: "monotone",
      legendWrapperStyle: {
        "font-size": sp ? "10px" : "15px",
        "margin-left": "20px",
        "line-height": "40px",
      },
      series: [
        {
          key: "population",
          name: "人口推計",
          stroke: "#FF367F",
          fill: "#FF367F",
          strokeWidth: sp ? 2 : 3,
          unit: "千人",
        },
      ],
      containerStyle: "width:100%;height:100%;min-width:0",
    };
  }
}

application.register(
  "site--prefecture-area--population-change",
  SitePrefectureAreaPopulationChangeController,
);
